import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import StudentLayout from '../../components/layout/StudentLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import testService from '../../services/testService';
import { fetchRelatedStudentsForAssignment } from '../../services/relatedStudentsService';
import { EXAM_PREP_PLANS, SUBJECTS, BOARDS, ACADEMIC_YEARS, SEMESTER_TEMPLATES } from '../../config/Config';
import TopicSequencePicker from '../../components/common/TopicSequencePicker';
import { filterPublished } from '../../utils/publishedFilter';
import {
  fetchUnifiedTeacherStudents,
  formatTeacherStudentOptionLabel,
  getManagedStudentRecordId,
  getPlatformStudentPk,
  isManagedRosterStudent,
} from '../../utils/teacherStudentRoster';

async function examPrepFetchPaginated(endpoint) {
  let url = endpoint;
  let all = [];
  while (url) {
    const res = await api.get(url);
    const data = res.data;
    if (Array.isArray(data)) {
      all = data;
      break;
    }
    all = all.concat(data.results || []);
    url = data.next || null;
  }
  return all;
}

function sessionStateChip(state) {
  const map = {
    active: { label: 'Active', color: 'success' },
    locked: { label: 'Locked', color: 'default' },
    completed: { label: 'Done', color: 'primary' },
    missed: { label: 'Missed', color: 'warning' },
    skipped: { label: 'Skipped', color: 'default' },
  };
  const m = map[state] || { label: state, color: 'default' };
  return <Chip size="small" label={m.label} color={m.color} variant={state === 'locked' ? 'outlined' : 'filled'} />;
}

function planStatusChip(status) {
  const key = String(status || '').toLowerCase();
  const map = {
    active: { label: 'Active', color: 'success' },
    completed: { label: 'Completed', color: 'primary' },
    cancelled: { label: 'Cancelled', color: 'default' },
    draft: { label: 'Draft', color: 'warning' },
  };
  const m = map[key] || { label: status || '—', color: 'default' };
  return (
    <Chip
      size="small"
      label={m.label}
      color={m.color}
      variant={key === 'cancelled' || key === 'draft' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 600, textTransform: 'capitalize' }}
    />
  );
}

function formatExamDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Relative time for created_at. Not rendered in the list anymore; kept so dev HMR / stale bundles
 * that still call this do not throw ReferenceError.
 */
// eslint-disable-next-line no-unused-vars -- see JSDoc above
function formatRelativeUpdated(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatExamDate(iso);
}

/** List row: API may return 0–100 with one decimal. */
function planListProgressPercent(plan) {
  const n = Number(plan?.progress_percent);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function parseExamDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfTodayMs() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function upcomingFirstDateSort(aDate, bDate) {
  const today = startOfTodayMs();
  const a = parseExamDate(aDate)?.getTime();
  const b = parseExamDate(bDate)?.getTime();
  const aValid = Number.isFinite(a);
  const bValid = Number.isFinite(b);
  if (!aValid && !bValid) return 0;
  if (!aValid) return 1;
  if (!bValid) return -1;
  const aPast = a < today;
  const bPast = b < today;
  if (aPast !== bPast) return aPast ? 1 : -1;
  return aPast ? b - a : a - b;
}

/** Lesson gate topics: session.topic_ids, or full plan.topic_ids when session lists none (matches backend). */
function sessionLessonTopicIds(session, plan) {
  if (Array.isArray(session.topic_ids) && session.topic_ids.length) {
    return session.topic_ids.map(Number);
  }
  if (plan && Array.isArray(plan.topic_ids)) {
    return plan.topic_ids.map(Number);
  }
  return [];
}

/** Session test is available only after every gated topic has lesson_marked_complete. */
function sessionTopicsLessonsComplete(session, baselineByTopicId, plan) {
  const ids = sessionLessonTopicIds(session, plan);
  if (ids.length === 0) return true;
  return ids.every((tid) => baselineByTopicId[tid]?.lesson_marked_complete);
}

function examPrepStudentAccountId(student) {
  return getPlatformStudentPk(student) || student?.student_pk || student?.linked_student || null;
}

function examPrepManagedStudentId(student) {
  return isManagedRosterStudent(student) ? getManagedStudentRecordId(student) : null;
}

function formatExamPrepApiError(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (typeof data === 'object') {
    return Object.entries(data)
      .map(([key, value]) => {
        const message = Array.isArray(value) ? value.join(' ') : String(value);
        return key === 'non_field_errors' ? message : `${key}: ${message}`;
      })
      .join(' ');
  }
  return '';
}

export default function ExamPrepPlannerPage({ teacherMode = false, parentMode = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [planList, setPlanList] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [planDetail, setPlanDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    exam_name: '',
    exam_date: '',
    subject: '',
    board: '',
    academic_year: '',
    mode: 'balanced',
    percent_basic: 50,
    percent_advanced: 50,
  });
  const [prepTestMyQuestions, setPrepTestMyQuestions] = useState(false);
  const [topicSource, setTopicSource] = useState('custom');
  const [boardsCatalog, setBoardsCatalog] = useState([]);
  const [yearsCatalog, setYearsCatalog] = useState([]);
  const [semesterTemplateOptions, setSemesterTemplateOptions] = useState([]);
  const [selectedSemesterTemplateId, setSelectedSemesterTemplateId] = useState('');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [relatedStudents, setRelatedStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [listSearch, setListSearch] = useState('');
  const [listStatusFilter, setListStatusFilter] = useState('all');
  const [listSort, setListSort] = useState('exam_date_asc');
  const [markingTopicId, setMarkingTopicId] = useState(null);
  const [detailTab, setDetailTab] = useState('sessions');

  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isStudentViewer = !teacherMode && !parentMode;

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(EXAM_PREP_PLANS);
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setPlanList(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load exam prep plans.');
      setPlanList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlanDetail = useCallback(async (planId) => {
    if (!planId) return;
    setDetailLoading(true);
    setError('');
    try {
      const res = await api.get(`${EXAM_PREP_PLANS}${planId}/`);
      setPlanDetail(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load plan.');
      setPlanDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!selectedPlanId) {
      setPlanDetail(null);
      return;
    }
    setPlanDetail(null);
    loadPlanDetail(selectedPlanId);
  }, [selectedPlanId, loadPlanDetail]);

  useEffect(() => {
    if (!openCreate) return;
    (async () => {
      try {
        const r = await api.get(SUBJECTS);
        const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
        setSubjects(list);
      } catch {
        setSubjects([]);
      }
    })();
  }, [openCreate]);

  useEffect(() => {
    if (!openCreate || (!teacherMode && !parentMode)) return;
    let cancelled = false;
    (async () => {
      setLoadingStudents(true);
      try {
        const list = teacherMode
          ? (await fetchUnifiedTeacherStudents(api))
              .filter((s) => s.is_active !== false && (examPrepStudentAccountId(s) || examPrepManagedStudentId(s)))
              .map((s) => {
                const platformId = examPrepStudentAccountId(s);
                const managedId = examPrepManagedStudentId(s);
                const id = platformId || `managed-${managedId}`;
                return {
                  ...s,
                  id,
                  exam_prep_student_id: platformId || null,
                  exam_prep_managed_student_id: managedId || null,
                  label: formatTeacherStudentOptionLabel(s) || `Student #${id}`,
                };
              })
          : await fetchRelatedStudentsForAssignment({ teacherMode, parentMode });
        if (!cancelled) setRelatedStudents(list);
      } catch {
        if (!cancelled) {
          setRelatedStudents([]);
          setError('Failed to load students.');
        }
      } finally {
        if (!cancelled) setLoadingStudents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openCreate, teacherMode, parentMode]);

  useEffect(() => {
    if (!openCreate || !(teacherMode || parentMode)) return;
    let cancelled = false;
    (async () => {
      try {
        const [b, y] = await Promise.all([
          examPrepFetchPaginated(BOARDS),
          examPrepFetchPaginated(ACADEMIC_YEARS),
        ]);
        if (!cancelled) {
          setBoardsCatalog(filterPublished(b));
          setYearsCatalog(filterPublished(y));
        }
      } catch {
        if (!cancelled) {
          setBoardsCatalog([]);
          setYearsCatalog([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openCreate, teacherMode, parentMode]);

  useEffect(() => {
    if (!openCreate || !(teacherMode || parentMode) || topicSource !== 'semester_template') {
      if (!openCreate || topicSource !== 'semester_template') setSemesterTemplateOptions([]);
      return;
    }
    if (!form.board || !form.academic_year || !form.subject) {
      setSemesterTemplateOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          board: String(form.board),
          academic_year: String(form.academic_year),
          subject: String(form.subject),
        });
        const list = await examPrepFetchPaginated(`${SEMESTER_TEMPLATES}?${params.toString()}`);
        if (!cancelled) setSemesterTemplateOptions(list);
      } catch {
        if (!cancelled) setSemesterTemplateOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openCreate, teacherMode, parentMode, topicSource, form.board, form.academic_year, form.subject]);

  useEffect(() => {
    if (!openCreate || !form.subject) {
      setTopicOptions([]);
      if (!(teacherMode || parentMode)) setSelectedTopics([]);
      return;
    }
    if ((teacherMode || parentMode) && topicSource === 'custom' && (!form.board || !form.academic_year)) {
      setTopicOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingTopics(true);
      try {
        const opts = { subject: form.subject };
        if ((teacherMode || parentMode) && form.board && form.academic_year) {
          opts.board = form.board;
          opts.academicYear = form.academic_year;
        }
        const topics = await testService.fetchSubjectTopics(opts);
        if (!cancelled) {
          setTopicOptions(topics);
        }
      } catch {
        if (!cancelled) {
          setTopicOptions([]);
          setError('Failed to load topics for this subject.');
        }
      } finally {
        if (!cancelled) setLoadingTopics(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openCreate, form.subject, form.board, form.academic_year, teacherMode, parentMode, topicSource]);

  const resetCreateForm = () => {
    setForm({
      exam_name: '',
      exam_date: '',
      subject: '',
      board: '',
      academic_year: '',
      mode: 'balanced',
      percent_basic: 50,
      percent_advanced: 50,
    });
    setPrepTestMyQuestions(false);
    setTopicSource('custom');
    setSemesterTemplateOptions([]);
    setSelectedSemesterTemplateId('');
    setSelectedTopics([]);
    setTopicOptions([]);
    setSelectedStudent(null);
  };

  const handleCreate = async () => {
    const topicIds = selectedTopics.map((t) => t.id).filter((id) => id != null);
    if (!form.exam_name || !form.exam_date) {
      setError('Fill exam name and date.');
      return;
    }
    if ((teacherMode || parentMode) && !selectedStudent?.id) {
      setError('Select a student.');
      return;
    }
    if (teacherMode || parentMode) {
      if (!form.board || !form.academic_year || !form.subject) {
        setError('Select board, academic year, and subject.');
        return;
      }
      if (topicSource === 'semester_template') {
        if (!selectedSemesterTemplateId) {
          setError('Select a semester template or switch to custom topics.');
          return;
        }
      } else if (topicIds.length === 0) {
        setError('Add at least one topic, or use a semester template.');
        return;
      }
    } else if (!form.subject || topicIds.length === 0) {
      setError('Fill subject and at least one topic.');
      return;
    }
    const pb = Number(form.percent_basic);
    const pa = Number(form.percent_advanced);
    if (pb + pa !== 100) {
      setError('Basic and advanced question percentages must total 100%.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const body = {
        exam_name: form.exam_name,
        exam_date: form.exam_date,
        mode: form.mode,
        activate: true,
        percent_basic: pb,
        percent_advanced: pa,
        prep_test_my_questions: !!(teacherMode || parentMode) && prepTestMyQuestions,
      };
      if (teacherMode || parentMode) {
        if (selectedStudent.exam_prep_managed_student_id) {
          body.teacher_managed_student = selectedStudent.exam_prep_managed_student_id;
        } else {
          body.student = selectedStudent.exam_prep_student_id || selectedStudent.id;
        }
        body.board = Number(form.board);
        body.academic_year = Number(form.academic_year);
        body.subject = Number(form.subject);
        if (topicSource === 'semester_template') {
          body.semester_template = Number(selectedSemesterTemplateId);
        } else {
          body.topic_ids = topicIds;
        }
      } else {
        body.subject = Number(form.subject);
        body.topic_ids = topicIds;
      }
      const res = await api.post(EXAM_PREP_PLANS, body);
      setOpenCreate(false);
      resetCreateForm();
      await loadList();
      if (res.data?.id) {
        setSelectedPlanId(res.data.id);
      }
    } catch (e) {
      setError(formatExamPrepApiError(e?.response?.data) || 'Create failed.');
    } finally {
      setCreating(false);
    }
  };

  const completeWithoutTest = async (planId) => {
    try {
      await api.post(`${EXAM_PREP_PLANS}${planId}/complete-session-without-test/`);
      await loadPlanDetail(planId);
      await loadList();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not complete session.');
    }
  };

  const listInsights = useMemo(() => {
    const active = planList.filter((p) => p.status === 'active');
    const activeCount = active.length;
    let nearest = null;
    let nearestMs = null;
    active.forEach((p) => {
      const d = parseExamDate(p.exam_date);
      if (!d) return;
      const ms = d.getTime();
      if (ms < startOfTodayMs()) return;
      if (nearestMs === null || ms < nearestMs) {
        nearestMs = ms;
        nearest = p;
      }
    });
    const due7 = active.filter((p) => {
      const d = typeof p.days_until_exam === 'number' ? p.days_until_exam : 999;
      return d >= 0 && d <= 7;
    }).length;
    let avgDays = null;
    if (active.length > 0) {
      const sum = active.reduce(
        (s, p) => s + (typeof p.days_until_exam === 'number' ? p.days_until_exam : 0),
        0
      );
      avgDays = Math.round(sum / active.length);
    }
    return { activeCount, nearest, due7, avgDays };
  }, [planList]);

  const filteredPlans = useMemo(() => {
    if (isStudentViewer) {
      const list = [...planList];
      list.sort((a, b) => {
        return upcomingFirstDateSort(a.exam_date, b.exam_date);
      });
      return list;
    }
    let list = [...planList];
    const q = listSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => (p.exam_name || '').toLowerCase().includes(q));
    }
    if (listStatusFilter !== 'all') {
      list = list.filter((p) => p.status === listStatusFilter);
    }
    list.sort((a, b) => {
      const da = parseExamDate(a.exam_date)?.getTime() ?? 0;
      const db = parseExamDate(b.exam_date)?.getTime() ?? 0;
      if (listSort === 'exam_date_desc') return db - da;
      if (listSort === 'exam_date_asc') return upcomingFirstDateSort(a.exam_date, b.exam_date);
      if (listSort === 'days_asc') return (a.days_until_exam ?? 999) - (b.days_until_exam ?? 999);
      if (listSort === 'name_asc') return (a.exam_name || '').localeCompare(b.exam_name || '');
      return da - db;
    });
    return list;
  }, [planList, listSearch, listStatusFilter, listSort, isStudentViewer]);
  const studentOwnPlan =
    isStudentViewer &&
    user?.id != null &&
    planDetail?.student != null &&
    Number(planDetail.student) === Number(user.id) &&
    planDetail?.created_by != null &&
    Number(planDetail.created_by) === Number(user.id);

  const topicNameById = useMemo(() => {
    const m = {};
    (planDetail?.topic_catalog || []).forEach((t) => {
      m[Number(t.topic_id)] = t.name;
    });
    return m;
  }, [planDetail]);

  const topicBaselineById = useMemo(() => {
    const m = {};
    (planDetail?.topic_baselines || []).forEach((b) => {
      m[Number(b.topic_id)] = b;
    });
    return m;
  }, [planDetail]);

  const markTopicLesson = useCallback(
    async (topicId, completed) => {
      if (!planDetail?.id) return;
      setMarkingTopicId(topicId);
      setError('');
      try {
        await api.post(`${EXAM_PREP_PLANS}${planDetail.id}/mark-topic-lesson/`, {
          topic_id: topicId,
          completed,
        });
        await loadPlanDetail(planDetail.id);
        await loadList();
      } catch (e) {
        const d = e?.response?.data;
        setError(
          (typeof d?.detail === 'string' && d.detail) ||
            d?.topic_id?.[0] ||
            'Could not update lesson status.'
        );
      } finally {
        setMarkingTopicId(null);
      }
    },
    [planDetail, loadPlanDetail, loadList]
  );

  const sortedPlanSessions = useMemo(() => {
    return [...(planDetail?.sessions || [])].sort((a, b) => {
      const byDate = upcomingFirstDateSort(
        a.due_date || a.scheduled_date,
        b.due_date || b.scheduled_date
      );
      if (byDate !== 0) return byDate;
      return (a.sequence_order ?? 0) - (b.sequence_order ?? 0);
    });
  }, [planDetail?.sessions]);

  const linkedTestCount = useMemo(
    () => sortedPlanSessions.filter((session) => !!session.test).length,
    [sortedPlanSessions]
  );

  const detailView = selectedPlanId && planDetail && (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton aria-label="Back to plans" onClick={() => setSelectedPlanId(null)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          {planDetail.exam_name}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Exam {planDetail.exam_date} · {planDetail.subject_name || `Subject #${planDetail.subject}`} ·{' '}
        {planDetail.mode}
      </Typography>
      {(teacherMode || parentMode) && planDetail.student_name && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Assigned to {planDetail.student_name}
        </Typography>
      )}
      <>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {planDetail.days_until_exam} days to exam
              {planDetail.readiness_score != null &&
                ` · Readiness ${(planDetail.readiness_score * 100).toFixed(0)}%`}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={planDetail.progress_percent || 0}
            sx={{ mb: 2, width: '100%' }}
          />
          <Tabs
            value={detailTab}
            onChange={(event, value) => setDetailTab(value)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            aria-label="Exam prep detail tabs"
          >
            <Tab value="sessions" label={`Sessions (${sortedPlanSessions.length})`} />
            <Tab value="tests" label={`Tests (${linkedTestCount})`} />
          </Tabs>
          {detailTab === 'sessions' && (
          <Stack spacing={1.5} sx={{ width: '100%' }}>
            {sortedPlanSessions.map((s, index) => (
              <Card key={s.id} variant="outlined" sx={{ width: '100%' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                    {sessionStateChip(s.unlock_state)}
                    <Typography variant="body2" fontWeight={600}>
                      #{index + 1} {s.session_type.replace(/_/g, ' ')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Due {s.due_date || s.scheduled_date}
                    </Typography>
                    {false && s.test ? (
                      <Chip
                        size="small"
                        label={`${s.num_questions ?? '?'} Q · ${s.duration_minutes ?? '?'} min`}
                        variant="outlined"
                      />
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        <Chip
                          size="small"
                          label={`Target ${s.num_questions ?? '?'} Q · ${s.duration_minutes ?? '?'} min`}
                          variant="outlined"
                          color="warning"
                        />
                        {false && <Chip size="small" label="No test linked yet" variant="outlined" />}
                      </Stack>
                    )}
                  </Stack>
                  {Array.isArray(s.topic_labels) && s.topic_labels.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {s.topic_labels.join(' · ')}
                    </Typography>
                  )}
                  {s.test_title && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {s.test_title}
                    </Typography>
                  )}
                  {(() => {
                    const lessonTids = sessionLessonTopicIds(s, planDetail);
                    const showLessonToggles =
                      lessonTids.length > 0 &&
                      ((teacherMode || parentMode) ||
                        (studentOwnPlan && s.unlock_state === 'active'));
                    if (!showLessonToggles) return null;
                    return (
                      <Stack
                        spacing={1}
                        sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
                        >
                          Lesson completed
                        </Typography>
                        {lessonTids.map((topicId) => {
                          const label = topicNameById[topicId] || `Topic #${topicId}`;
                          const bl = topicBaselineById[topicId];
                          const done = !!bl?.lesson_marked_complete;
                          return (
                            <FormControlLabel
                              key={`${s.id}-topic-${topicId}`}
                              sx={{ mr: 0, ml: 0, alignItems: 'center' }}
                              control={
                                <Switch
                                  size="small"
                                  checked={done}
                                  onChange={(e) => markTopicLesson(topicId, e.target.checked)}
                                  disabled={markingTopicId === topicId}
                                  inputProps={{
                                    'aria-label': `${done ? 'Lesson done' : 'Mark lesson'} — ${label}`,
                                  }}
                                />
                              }
                              label={<Typography variant="body2">{label}</Typography>}
                            />
                          );
                        })}
                      </Stack>
                    );
                  })()}
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1, display: 'none' }}
                    flexWrap="wrap"
                    alignItems="center"
                  >
                    {isStudentViewer &&
                      s.unlock_state === 'active' &&
                      s.test &&
                      !s.has_submitted_attempt &&
                      sessionTopicsLessonsComplete(s, topicBaselineById, planDetail) && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            navigate(`/test/${s.test}/instructions`, {
                              state: { testTitle: s.test_title || `Session ${s.sequence_order}` },
                            })
                          }
                        >
                          Start test
                        </Button>
                      )}
                    {isStudentViewer &&
                      s.unlock_state === 'active' &&
                      s.test &&
                      !s.has_submitted_attempt &&
                      !sessionTopicsLessonsComplete(s, topicBaselineById, planDetail) && (
                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
                          {studentOwnPlan
                            ? 'Mark all lessons complete for this session before you can start the test.'
                            : 'Your teacher or parent must mark all lessons complete for this session before you can start the test.'}
                        </Typography>
                      )}
                    {isStudentViewer && s.unlock_state === 'active' && !s.test && !s.has_submitted_attempt && (
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 340 }}>
                        {sessionTopicsLessonsComplete(s, topicBaselineById, planDetail)
                          ? 'Session test will appear here once it is generated.'
                          : studentOwnPlan
                            ? 'Mark all lessons above for this session. A practice test will then be created and assigned.'
                            : 'Your teacher or parent must mark all lessons for this session before a test is created.'}
                      </Typography>
                    )}
                    {isStudentViewer && s.has_submitted_attempt && (
                      <Chip size="small" label="Test completed" color="success" variant="outlined" />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
          )}
          {detailTab === 'tests' && (
            <Stack spacing={1.5} sx={{ width: '100%' }}>
              {sortedPlanSessions.map((s, index) => {
                const lessonsComplete = sessionTopicsLessonsComplete(s, topicBaselineById, planDetail);
                return (
                  <Card key={s.id} variant="outlined" sx={{ width: '100%' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                          {sessionStateChip(s.unlock_state)}
                          <Typography variant="body2" fontWeight={600}>
                            #{index + 1} {s.session_type.replace(/_/g, ' ')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Due {s.due_date || s.scheduled_date}
                          </Typography>
                          {s.test ? (
                            <Chip
                              size="small"
                              label={`${s.num_questions ?? '?'} Q - ${s.duration_minutes ?? '?'} min`}
                              variant="outlined"
                            />
                          ) : (
                            <Chip size="small" label="No test linked yet" variant="outlined" />
                          )}
                          {s.has_submitted_attempt && (
                            <Chip size="small" label="Test completed" color="success" variant="outlined" />
                          )}
                        </Stack>
                        {isStudentViewer && s.unlock_state === 'active' && s.test && !s.has_submitted_attempt && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!lessonsComplete}
                            onClick={() =>
                              navigate(`/test/${s.test}/instructions`, {
                                state: { testTitle: s.test_title || `Session ${index + 1}` },
                              })
                            }
                          >
                            Start test
                          </Button>
                        )}
                      </Stack>
                      {Array.isArray(s.topic_labels) && s.topic_labels.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {s.topic_labels.join(' - ')}
                        </Typography>
                      )}
                      {s.test_title && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {s.test_title}
                        </Typography>
                      )}
                      {isStudentViewer && s.unlock_state === 'active' && s.test && !lessonsComplete && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          {studentOwnPlan
                            ? 'Complete the lesson items in the Sessions tab before starting this test.'
                            : 'The lesson items must be completed before this test can be started.'}
                        </Typography>
                      )}
                      {isStudentViewer && s.unlock_state === 'active' && !s.test && !s.has_submitted_attempt && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          {lessonsComplete
                            ? 'This test will appear once it is generated and linked.'
                            : 'Complete the lesson items in the Sessions tab to unlock test generation.'}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
          {detailTab === 'sessions' && isStudentViewer && planDetail.active_session_id && (
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => completeWithoutTest(planDetail.id)}
                disabled={
                  !planDetail.sessions?.some((s) => s.id === planDetail.active_session_id && !s.test)
                }
              >
                Mark active session done (no test)
              </Button>
            </Box>
          )}
      </>
    </Box>
  );

  const openNewPlanDialog = () => {
    resetCreateForm();
    setOpenCreate(true);
  };

  const daysToExamSx = (d) => {
    if (typeof d !== 'number') return { color: 'text.secondary' };
    if (d <= 3) return { color: 'error.main', fontWeight: 800 };
    if (d <= 14) return { color: 'warning.dark', fontWeight: 700 };
    return { color: 'text.primary', fontWeight: 700 };
  };

  const listSkeleton = (
    <Stack spacing={1.5} sx={{ mt: 1 }} aria-busy="true" aria-label="Loading exam prep plans">
      <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
      {[1, 2, 3, 4].map((k) => (
        <Skeleton key={k} variant="rounded" height={108} sx={{ borderRadius: 2 }} />
      ))}
    </Stack>
  );

  const listView = (
    <Box component="section" aria-labelledby="exam-prep-planner-sr-title">
      <Typography
        id="exam-prep-planner-sr-title"
        component="h1"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Exam prep planner
      </Typography>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openNewPlanDialog}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            flexShrink: 0,
            py: 1.25,
            fontWeight: 600,
          }}
        >
          New plan
        </Button>
      </Stack>

      {loading ? (
        listSkeleton
      ) : planList.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, sm: 5 },
            mt: 2,
            textAlign: 'center',
            borderRadius: 2,
            borderStyle: 'dashed',
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom>
            No exam prep plans yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
            Create a plan with your exam date and topics. You will get a clear schedule and short checks to stay on
            track.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewPlanDialog} size="large">
            Create your first plan
          </Button>
        </Paper>
      ) : (
        <>
          {!isStudentViewer && (
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                mb: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                    Active plans
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                    {listInsights.activeCount}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                    Nearest exam
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {listInsights.nearest ? formatExamDate(listInsights.nearest.exam_date) : '—'}
                  </Typography>
                  {listInsights.nearest && (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {listInsights.nearest.exam_name}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                    Due within 7 days
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                    {listInsights.due7}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                    Avg. days left (active)
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                    {listInsights.avgDays != null ? listInsights.avgDays : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {!isStudentViewer && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              sx={{ mb: 2 }}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <TextField
                size="small"
                placeholder="Search by exam name"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                inputProps={{ 'aria-label': 'Search plans by exam name' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: { md: 1 }, minWidth: { xs: '100%', md: 200 } }}
              />
              <TextField
                size="small"
                select
                label="Status"
                value={listStatusFilter}
                onChange={(e) => setListStatusFilter(e.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 140 } }}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
              <TextField
                size="small"
                select
                label="Sort"
                value={listSort}
                onChange={(e) => setListSort(e.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 200 } }}
              >
                <MenuItem value="exam_date_asc">Exam date (soonest first)</MenuItem>
                <MenuItem value="exam_date_desc">Exam date (latest first)</MenuItem>
                <MenuItem value="days_asc">Days to exam (fewest first)</MenuItem>
                <MenuItem value="name_asc">Exam name (A–Z)</MenuItem>
              </TextField>
            </Stack>
          )}

          {!isMdDown && (
            <Stack
              direction="row"
              spacing={2}
              sx={{
                px: { xs: 1, sm: 2 },
                py: 1,
                mb: 0.5,
                color: 'text.secondary',
                typography: 'caption',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>Exam</Box>
              <Box sx={{ width: 120, flexShrink: 0 }}>Exam date</Box>
              <Box sx={{ width: 140, flexShrink: 0 }}>
                {teacherMode || parentMode ? 'Assigned to' : 'Created by'}
              </Box>
              {!isStudentViewer && <Box sx={{ width: 100, flexShrink: 0 }}>Status</Box>}
              <Box sx={{ width: 88, flexShrink: 0, textAlign: 'right' }}>Days left</Box>
              <Box sx={{ width: 120, flexShrink: 0 }}>Progress</Box>
              <Box sx={{ width: 36, flexShrink: 0 }} aria-hidden />
            </Stack>
          )}

          {filteredPlans.length === 0 && !isStudentViewer ? (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                No plans match your filters
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Try clearing search or choosing &ldquo;All statuses&rdquo;.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setListSearch('');
                  setListStatusFilter('all');
                }}
              >
                Reset filters
              </Button>
            </Paper>
          ) : filteredPlans.length === 0 ? null : (
            <Stack
              component="ul"
              spacing={1.25}
              sx={{ listStyle: 'none', p: 0, m: 0 }}
              aria-label="Exam prep plans"
            >
              {filteredPlans.map((p) => {
                const days = p.days_until_exam;
                const showUrgent = typeof days === 'number' && days <= 7 && p.status === 'active';
                const progressPct = planListProgressPercent(p);
                const progressLabel =
                  Math.abs(progressPct - Math.round(progressPct)) < 0.05
                    ? `${Math.round(progressPct)}%`
                    : `${progressPct.toFixed(1)}%`;
                const assigneeName =
                  teacherMode || parentMode ? p.student_name || '—' : p.created_by_name || '—';
                return (
                  <Card
                    key={p.id}
                    component="li"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      transition: theme.transitions.create(['box-shadow', 'border-color', 'background-color'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      '&:hover': {
                        boxShadow: theme.shadows[2],
                        borderColor: 'primary.light',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => setSelectedPlanId(p.id)}
                      aria-label={`Open plan ${p.exam_name}, exam date ${formatExamDate(p.exam_date)}, ${progressLabel} complete`}
                      sx={{
                        alignItems: 'stretch',
                        borderRadius: 2,
                        '&:focus-visible': {
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          py: { xs: 1.75, sm: 2 },
                          px: { xs: 2, sm: 2.5 },
                          '&:last-child': { pb: { xs: 1.75, sm: 2 } },
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={2}
                          alignItems={{ xs: 'flex-start', md: 'center' }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                              {isMdDown && !isStudentViewer && planStatusChip(p.status)}
                              {showUrgent && (
                                <Chip size="small" label="Soon" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                              )}
                            </Stack>
                            <Typography
                              variant="h6"
                              component="div"
                              fontWeight={700}
                              sx={{ mt: showUrgent || (isMdDown && !isStudentViewer) ? 0.75 : 0, lineHeight: 1.3 }}
                            >
                              {p.exam_name}
                            </Typography>
                            {p.subject_name && (
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {p.subject_name}
                                {p.mode ? ` · ${String(p.mode).replace(/_/g, ' ')}` : ''}
                              </Typography>
                            )}
                          </Box>

                          {isMdDown ? (
                            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap width="100%" alignItems="center">
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {formatExamDate(p.exam_date)}
                                </Typography>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 120 }}>
                                <Typography variant="body2" fontWeight={500} noWrap title={assigneeName}>
                                  {assigneeName}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                <Typography variant="h5" component="span" sx={daysToExamSx(days)}>
                                  {typeof days === 'number' ? days : '—'}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 100, maxWidth: 160, flex: '1 1 100px' }}>
                                <Typography variant="body2" fontWeight={700} component="div">
                                  {progressLabel}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={progressPct}
                                  sx={{
                                    mt: 0.5,
                                    height: 6,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                  }}
                                />
                              </Box>
                            </Stack>
                          ) : (
                            <>
                              <Box sx={{ width: 120, flexShrink: 0 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {formatExamDate(p.exam_date)}
                                </Typography>
                              </Box>
                              <Box sx={{ width: 140, flexShrink: 0 }}>
                                <Typography variant="body2" fontWeight={500} noWrap title={assigneeName}>
                                  {assigneeName}
                                </Typography>
                              </Box>
                              {!isStudentViewer && (
                                <Box sx={{ width: 100, flexShrink: 0 }}>{planStatusChip(p.status)}</Box>
                              )}
                              <Box sx={{ width: 88, flexShrink: 0, textAlign: 'right' }}>
                                <Typography variant="h5" component="span" sx={daysToExamSx(days)}>
                                  {typeof days === 'number' ? days : '—'}
                                </Typography>
                              </Box>
                              <Box sx={{ width: 120, flexShrink: 0, alignSelf: 'center' }}>
                                <Typography variant="body2" fontWeight={700} textAlign="right" component="div">
                                  {progressLabel}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={progressPct}
                                  sx={{
                                    mt: 0.5,
                                    height: 6,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                  }}
                                />
                              </Box>
                            </>
                          )}

                          <ChevronRightIcon
                            sx={{
                              color: 'action.active',
                              alignSelf: 'center',
                              display: { xs: 'none', md: 'block' },
                            }}
                            aria-hidden
                          />
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Box>
  );

  const inner = (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: 'none',
        mx: 0,
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {selectedPlanId ? detailView || (detailLoading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : null) : listView}

      <Dialog
        open={openCreate}
        onClose={() => !creating && setOpenCreate(false)}
        maxWidth={teacherMode || parentMode ? 'md' : 'sm'}
        fullWidth
      >
        <DialogTitle>New exam prep plan</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(teacherMode || parentMode) && (
              <Autocomplete
                options={relatedStudents}
                loading={loadingStudents}
                value={selectedStudent}
                onChange={(e, v) => setSelectedStudent(v)}
                getOptionLabel={(o) => o?.label || formatTeacherStudentOptionLabel(o) || ''}
                isOptionEqualToValue={(a, b) => String(a?.id || '') === String(b?.id || '')}
                disabled={loadingStudents || creating}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={parentMode ? 'Child' : 'Student'}
                    required
                    helperText={loadingStudents ? 'Loading…' : ''}
                  />
                )}
              />
            )}
            <TextField
              label="Exam name"
              value={form.exam_name}
              onChange={(e) => setForm((f) => ({ ...f, exam_name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Exam date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.exam_date}
              onChange={(e) => setForm((f) => ({ ...f, exam_date: e.target.value }))}
              fullWidth
              required
            />
            {(teacherMode || parentMode) && (
              <>
                <TextField
                  select
                  label="Board"
                  value={form.board}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({ ...f, board: v }));
                    setSelectedTopics([]);
                    setSelectedSemesterTemplateId('');
                  }}
                  fullWidth
                  required
                >
                  {boardsCatalog.map((b) => (
                    <MenuItem key={b.id} value={String(b.id)}>
                      {b.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Academic year"
                  value={form.academic_year}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({ ...f, academic_year: v }));
                    setSelectedTopics([]);
                    setSelectedSemesterTemplateId('');
                  }}
                  fullWidth
                  required
                >
                  {yearsCatalog.map((y) => (
                    <MenuItem key={y.id} value={String(y.id)}>
                      {y.year}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
            <TextField
              select
              label="Subject"
              value={form.subject}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, subject: v }));
                setSelectedTopics([]);
                setSelectedSemesterTemplateId('');
              }}
              fullWidth
              required
            >
              {subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            {(teacherMode || parentMode) && (
              <FormControl disabled={creating}>
                <FormLabel component="legend">Topics</FormLabel>
                <RadioGroup
                  row
                  value={topicSource}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTopicSource(v);
                    setSelectedTopics([]);
                    setSelectedSemesterTemplateId('');
                  }}
                >
                  <FormControlLabel value="semester_template" control={<Radio />} label="Semester template" />
                  <FormControlLabel value="custom" control={<Radio />} label="Custom topic list" />
                </RadioGroup>
              </FormControl>
            )}
            {(teacherMode || parentMode) && topicSource === 'semester_template' && (
              <TextField
                select
                label="Semester template"
                value={selectedSemesterTemplateId}
                onChange={(e) => setSelectedSemesterTemplateId(e.target.value)}
                fullWidth
                required
                disabled={creating || !form.board || !form.academic_year || !form.subject}
                helperText={
                  !form.board || !form.academic_year || !form.subject
                    ? 'Choose board, year, and subject to list templates.'
                    : semesterTemplateOptions.length === 0
                      ? 'No templates for this combination. Use custom topics or ask an admin to add one.'
                      : ' '
                }
              >
                {semesterTemplateOptions.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {(!(teacherMode || parentMode) || topicSource === 'custom') && (
              <TopicSequencePicker
                topicOptions={topicOptions}
                value={selectedTopics}
                onChange={setSelectedTopics}
                disabled={creating}
                loading={loadingTopics}
                subjectSelected={
                  !!form.subject &&
                  (!(teacherMode || parentMode) || (!!form.board && !!form.academic_year))
                }
                showQuestionCount={false}
                helperTextWhenNoSubject={
                  teacherMode || parentMode
                    ? 'Select board, academic year, and subject first'
                    : 'Select a subject first'
                }
              />
            )}
            {(teacherMode || parentMode) && (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Question mix: Basic %"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    value={form.percent_basic}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                      setForm((f) => ({ ...f, percent_basic: v, percent_advanced: 100 - v }));
                    }}
                    fullWidth
                    helperText="Must total 100% with Advanced"
                  />
                  <TextField
                    label="Advanced %"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    value={form.percent_advanced}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                      setForm((f) => ({ ...f, percent_advanced: v, percent_basic: 100 - v }));
                    }}
                    fullWidth
                  />
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={prepTestMyQuestions}
                      onChange={(e) => setPrepTestMyQuestions(e.target.checked)}
                      disabled={creating}
                    />
                  }
                  label="Use only my questions for auto-generated tests"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Same settings as auto-generate test: board, year, and difficulty mix apply to initial session
                  tests. Adaptive updates can assign different tests later based on performance.
                </Typography>
              </>
            )}
            <TextField
              select
              label="Mode"
              value={form.mode}
              onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
              fullWidth
            >
              <MenuItem value="balanced">Balanced</MenuItem>
              <MenuItem value="weak_focus">Weak focus</MenuItem>
              <MenuItem value="intensive">Intensive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCreate(false);
              resetCreateForm();
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (teacherMode || parentMode) {
    return inner;
  }
  return <StudentLayout>{inner}</StudentLayout>;
}
